import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from "rxjs";


import moment from "moment";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";

import {FormControl} from "@angular/forms";
import {filter, tap} from "rxjs/operators";
// @ts-ignore
import * as Diacritics from "diacritics";

import type {IProgressPaginator} from '@shared/services/global/excel.service';
import {
  GOOGLE_SHEET_OPEN_MODAL_EXPORT,
  IConfigExportDataGoogleSheet,
  IOptionsExportDataSetting
} from '@shared/models/business';
import {IDisplayColumn} from '@shared/models/idb';
import {IDataExportSpreadData} from '@core/models';
import {COMMON_DATE_MOMENT, FILE_NAME_DATE_FORMAT} from '@shared/constants';
import {
  ExcelDataAbstractService,
  ITabRefExportDataSetting
} from '@shared/components/excel/excel-data-abstract.service';


const TYPE_USE = {
  NEW: 1, // File mới hoặc sheetName mới
  AVAILABLE: 2, // File đã có hoặc sheetName đã có
}
@Directive()
export abstract class ExportDataSettingBase extends ExcelDataAbstractService implements OnInit, OnDestroy {
  @Input() override options: IOptionsExportDataSetting = {} as IOptionsExportDataSetting
  @Output() output$ = new EventEmitter<IConfigExportDataGoogleSheet>();

  state !: IConfigExportDataGoogleSheet;

  isCheckAll = false;
  controlKeypress = new FormControl();
  isResultSearch = false;
  protected cdr = inject(ChangeDetectorRef);
  protected abstract readonly exportType: ITabRefExportDataSetting;

  ngOnInit() {
    this._initStateConfig();

    this.handleCheckAll();
    this.buildSearch();

    if (this.options.excelService) {
      this.options.excelService.progressPaginator.pipe(takeUntil(this.appForm.destroyed)).subscribe({
        next: res => {
          if (res == null) {
            // Handler finnal
            return;
          }
          const cal = res.page * res.pageSize;
          this.progressState.totalItems = res.totalItems;
          // Nếu không có totalItems (như ElasticSearch), hiển thị cal thay vì gán về 0
          this.progressState.currentItem = res.currentItem ?? ((res.totalItems > 0 && cal > res.totalItems) ? res.totalItems : cal);
          this.progressState.totalPages = res.totalPages;
          this.progressState.currentPage = res.page;
          this.progressState.spreadData = res.spreadData ?? res.resultData as IDataExportSpreadData;
          this.progressState.isError = !!res.isError;

          if (res.isError) {
            this.progressState.isExportSubmit = false;
          }

          this.onProgress(res);

          // Ép Angular cập nhật UI ngay sau khi thay đổi state (tránh lỗi NG0100)
          this.cdr.detectChanges();
        }
      })

      this.options.excelService.subjectEvent$.pipe(takeUntil(this.appForm.destroyed)).subscribe({
        next: res => {
          if (res.type == 'error') {
            this.progressState.isExportSubmit = false;
            this.onExportError(res.res);
            this.cdr.detectChanges();
          }
        }
      })
    }
  }

  ngOnDestroy() {
    this.appForm.navService.removeParams([GOOGLE_SHEET_OPEN_MODAL_EXPORT])
    this.appForm.getDestroySubs()
  }

  onChangeValue(key: string, value: any) {
    if (this.errors && this.errors[key] && value) {
      delete this.errors[key];
    }
  }

  /**
   * ---------------------------------------------------------------------------------------------------------------------
   * Funcs init ----------------------------------------------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------------------
   * */
  private _initStateConfig() {
    const opt$ = this.options;
    const fileName = opt$.configFile.fileName + `_${moment().format(FILE_NAME_DATE_FORMAT)}`;

    this.state = {
      link: this.appForm.navService.currentUri,
      tabRef: this.exportType,
      businessId: opt$.businessId,
      pageSelection: opt$.excelListPage ? opt$.excelListPage.checked : 'recordAll', // Chọn kiểu xuất dữ liệu
      settingId: undefined, // Mẫu cài đặt đã lưu
      accessTokenId: undefined, // Export dùng tài khoản Google đã lưu của user, BE tự lấy token
      // Dùng chung cả "Excel | Google Sheet"
      typeUseFile: TYPE_USE.NEW, // 1 Mới | 2 Đã có
      fileNew: {
        fileName: fileName,
        sheetName: '',
      },
      googleFile: {
        typeUseSheet: TYPE_USE.NEW, // Loại sheet đang dùng
        spreadUrl: '',
        spreadSheetNewTitle: '',
        spreadSheetId: 0,
        spreadSheetTitle: '',
      },
      columns: opt$.columns ? opt$.columns.map(col => ({...col})) : [],
      rowDataLoop: false
    }

    setTimeout(() => {
      let sheetTranslate = this.appForm.appService.translate(opt$.configFile.sheetName);
      sheetTranslate += ` ${moment().format(COMMON_DATE_MOMENT)}`
      this.state.fileNew.sheetName = sheetTranslate;
      this.state.googleFile.spreadSheetNewTitle = sheetTranslate;
    }, 1000)
  }

  abstract onExportData(): void;
  protected abstract onProgress(progress: IProgressPaginator): void;

  protected onExportError(response: unknown) {
    const messages = (response as {messages?: unknown} | null)?.messages;
    if (!messages || typeof messages !== 'object' || Array.isArray(messages)) return;

    const fields: Record<string, string> = {
      fileName: 'fileNew.fileName',
      sheetName: this.exportType === 'googleSheets' ? 'spreadSheetNewTitle' : 'fileNew.sheetName',
      spreadUrl: 'spreadId',
    };
    Object.entries(fields).forEach(([field, key]) => {
      const value = (messages as Record<string, unknown>)[field];
      const message = Array.isArray(value) ? value.filter(item => typeof item === 'string').join(', ')
        : typeof value === 'string' ? value : '';
      if (message) this.errors[key] = message;
    });
  }

  /** Thay đổi cột xuất trong lần xuất hiện tại. */
  colChange(value: any, col: IDisplayColumn) {
    if (col.required) {
      return;
    }
    col.display = value == 1;
    this.handleCheckAll();
  }

  /**
   * Thay đổi sắp xếp hiển thị
   * */
  dropDragField(event: CdkDragDrop<IDisplayColumn[]>) {
    if (this.options.allowColumnSelection === false || this.controlKeypress.value || this.progressState.isExportSubmit) return;
    moveItemInArray(this.state.columns, event.previousIndex, event.currentIndex);
  }

  /**
   * - Hiện tất cả cột
   * - Ẩn tất cả cột
   *
   * @Note: Đối với cột required thì không thay đổi
   * */
  onCheckAll() {
    const checked = !this.isCheckAll;
    this.isCheckAll = checked;
    this.state.columns.forEach((f: any) => {
      f.display = f.required ? true : checked;

      if (f.childs) {
        f.childs.forEach((fc: any) => {
          fc.display = fc.required ? true : checked;
        })
      }
    })
  }

  /**
   * Kiểm tra xem có đang check all hiển thị thay đổi icon
   * */
  private handleCheckAll() {
    this.isCheckAll = this.state.columns.every((f: any) => {
      if (f.childs) {
        // Kiểm tra tất cả phần tử con
        return f.childs.every((fc: any) => fc.display || fc.required);
      } else {
        // Kiểm tra phần tử cha
        return f.display || f.required;
      }
    });
  }

  private buildSearch() {
    this.controlKeypress.valueChanges.pipe(
      takeUntil(this.appForm.destroyed),
      startWith(''),
      distinctUntilChanged(),
      debounceTime(50),
      tap(value => {
        if (typeof value === "string" && value.trim() == '') this.resetSearch();
      }),
      filter(value => value != null && value.trim() != ''),
    ).subscribe({
      next: key => {
        this.keypressSearch(key)
      }
    });

  }

  /**
   * Hàm chỉ xử lý update lại "filterHide" để đánh dấu cột phù hợp tiêu chí tìm kiếm
   * */
  private keypressSearch(key: string) {
    this.isResultSearch = false;
    let query = Diacritics.remove(key.toLowerCase())
    query = query.trimEnd()
    query = query.trimStart()

    this.state.columns = this.state.columns.map((f: any) => ({
      ...this._compareSearch(query, f),
      childs: f.childs ? [...f.childs.map((fc: any) => ({...this._compareSearch(query, fc)}))] : undefined,
    }))
  }

  private _compareSearch(q: string, f: IDisplayColumn): IDisplayColumn {
    const name = this._getNameCol(f);
    const normal = Diacritics.remove(name.toLowerCase())
    const has = !normal.includes(q);
    if (!has) {
      this.isResultSearch = true;
    }
    f['filterHide'] = has;
    return {...f};
  }

  private _getNameCol(f: IDisplayColumn) {
    if (f.nameOriginal) {
      return f.nameOriginal;
    }

    if (f.name && f.name != '#') {
      return this.appForm.appService.translate(f.name)
    }

    return '';
  }

  /**
   * Reset search: xóa hết filterHide trong settingColumns
   * */
  resetSearch() {
    this.controlKeypress.setValue('', {emitEvent: false});
    this.isResultSearch = false;
    this.state.columns.forEach((f: any) => {
      f['filterHide'] = false

      if (f.childs) {
        f.childs.forEach((fc: any) => {
          fc['filterHide'] = false
        })
      }
    })
  }


  readonly TYPE_USE = TYPE_USE;
}
